import React from 'react'
import { Link, useLocation } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { CalendarOutlined, ClockCircleOutlined } from '@ant-design/icons'

import TranslationNS from '@/utils/Constants/TranslationNS'
import StatusDot from '@/components/common/StatusDot'
import { useEventHub } from './EventHubContext'
import { formatLongDate, getEventHubBase, getSidebarRegState, getTimezoneText } from './eventHub.utils'
import TicketSVG from '@/images/svgs/Ticket.svg?react'
import EventHubSidebarMap from './EventHubSidebarMap'
import SButtonLink from '../common/SButtonLink'

/** Mock `.ev-h`: Comp Medium 20 on a 16px-padded hairline; `.ev-h.reg` sits 32px under the block above. */
function SectionTitle({ icon, children, className = '' }) {
    return (
        <div
            className={`font-meta flex items-center gap-2 border-b border-[#dbdadf] pb-4 text-[20px] font-medium leading-[29px] text-[#272727] ${className}`}
        >
            {icon}
            <span>{children}</span>
        </div>
    )
}

/** Mock `.ev-dates`: SFT display 14, labels in --ink-2, dates in strong ink, 16px under the title. */
function DateRange({ startLabel, start, endLabel, end, className = '' }) {
    return (
        <div className={`font-display flex justify-between gap-3 text-sm text-[#5b5f6e] ${className}`}>
            {start && (
                <span>
                    {startLabel} - <span className="text-[#272727]">{start}</span>
                </span>
            )}
            {end && (
                <span>
                    {endLabel} - <span className="text-[#272727]">{end}</span>
                </span>
            )}
        </div>
    )
}

/** Mock `.ev-qual svg`: 16px trophy in strong ink (inline path from the prototype). */
function TrophySVG({ className = '' }) {
    return (
        <svg
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
            className={className}
        >
            <path d="M7 4h10v5a5 5 0 0 1-10 0z" />
            <path d="M7 6H4.5a1 1 0 0 0-1 1v1a3 3 0 0 0 3 3H7M17 6h2.5a1 1 0 0 1 1 1v1a3 3 0 0 1-3 3H17" />
            <path d="M12 14v3M8.5 20.5h7M10 17h4v3.5h-4z" />
        </svg>
    )
}

/**
 * Shared, READ-ONLY side panel. Rendered once by EventHub.jsx (never per tab).
 * Event date + timezone → Register / Spectator tickets / Join Staff CTAs → Registration date →
 * "Qualifying for" → map + address.
 */
export default function EventHubSidebar() {
    const { competition, presentation, eventIdentifier } = useEventHub()
    const { t, i18n } = useTranslation(TranslationNS.EVENT_PAGE)
    const { t: tHome } = useTranslation(TranslationNS.HOME_PAGE)
    const { t: tMonths } = useTranslation(TranslationNS.MONTHS)
    const { pathname } = useLocation()
    const language = i18n.resolvedLanguage

    const base = getEventHubBase(pathname, eventIdentifier)
    const tz = competition?.timezone

    const start = formatLongDate(competition?.date?.start?.iso, tz, language)
    const end = formatLongDate(competition?.date?.end?.iso, tz, language)

    // Shared with EventHubRegBar (mobile fixed bar) so both read the same state.
    const {
        regOpen,
        regSoon,
        regSoldOut,
        volOpen,
        volSoon,
        specOpen,
        specSoon,
        specSoldOut,
        regColor,
        regLabel,
        volSoonLabel,
        specColor,
        specLabel,
        regStart,
        regEnd,
    } = getSidebarRegState(competition, tHome, tMonths, language)
    // A CTA renders only when its registration is open (link), upcoming (info button) or
    // sold out (status slot); closed states show no button at all.
    const showButtons = regOpen || regSoon || regSoldOut || specOpen || specSoon || specSoldOut || volOpen || volSoon
    // Mock: the registration-date block stays whenever dates exist, upcoming included.
    const showRegDates = regStart || regEnd

    // "Qualifying for → <event>" (mock `.ev-qual`, #qual=): `_nextEvent` is populated with
    // { name, eventNumber, publish, buttonLink } by the presentation endpoint; hidden while
    // the next event is unpublished or when only its id is known.
    const nextEvent = competition?._nextEvent
    const qualifyingFor =
        nextEvent && typeof nextEvent === 'object' && nextEvent.name && nextEvent.publish !== false
            ? { name: nextEvent.name, to: `/${nextEvent.buttonLink?.url || nextEvent.eventNumber}` }
            : null

    const address = [presentation?.location, presentation?.country].filter(Boolean).join(', ')

    // mock .ev-slot: 44px bordered row, 13px, status dot + word
    const slotRow =
        'flex h-11 w-full items-center gap-2 rounded-[5px] border border-[#dbdadf] px-3 text-[13px] text-[#272727]'

    // Mock `.ev-aside` (Figma 3790:26239): radius 10, shadow 0 2 5, padding 24/18/16; dates 16px under
    // the title, timezone 4px under the dates, first CTA 16px lower, CTAs 18px apart, the next
    // block (registration dates, map) 32px lower, address 16px under the map.
    return (
        <aside className="flex flex-col rounded-[10px] border border-[#dbdadf] bg-white px-4.5 pb-4 pt-6 nav:shadow-[0_2px_5px_rgba(14,16,23,0.1)]">
            {/* Event date */}
            <SectionTitle icon={<CalendarOutlined className="text-base" />}>{t('Event date')}</SectionTitle>
            <DateRange className="mt-4" startLabel={t('Starts')} start={start} endLabel={t('End')} end={end} />
            {tz && (
                <div className="mt-1 flex items-center gap-1.5 text-[10px] text-[#9498a6]">
                    <ClockCircleOutlined className="text-[11px]" />
                    <span>
                        {t('Time zone')} - {getTimezoneText(tz, competition?.date?.start?.iso)}
                    </span>
                </div>
            )}

            {/* CTAs — open ⇒ link; upcoming ⇒ bordered "● Open <date>" slot rows (mock #regSoon);
                sold out ⇒ "● Sold Out" row + outlined waiting-list link (mock #regFull).
                Order: athletes → spectators → staff (the two paying audiences first). */}
            {/* id: EventHubRegBar watches this block and takes over once it leaves the viewport (mobile). */}
            {showButtons && (
                <div id="eh-reg-cta" className="mt-4 flex flex-col gap-[18px]">
                    {regOpen && (
                        <SButtonLink value={t('Register Now')} to={`${base}/registration`} className={'w-full'} />
                    )}
                    {regSoon && (
                        <span className={slotRow}>
                            <StatusDot color="--status-openat" className="size-[7px]" />
                            <span>{regLabel}</span>
                        </span>
                    )}
                    {/* "● Sold Out" + "Join the waiting list" (William, 22 Sept. / 8 Oct. 2026): the CTA
                        shows as soon as the event is full; the registration page lists each division's
                        waiting list and says so when they are all closed. */}
                    {regSoldOut && (
                        <>
                            <span className={slotRow}>
                                <StatusDot color={regColor} className="size-[7px]" />
                                <span>{regLabel}</span>
                            </span>
                            <SButtonLink
                                value={t('Join the waiting list')}
                                to={`${base}/registration`}
                                className={'w-full'}
                                outlined
                            />
                        </>
                    )}
                    {/* Spectator tickets (William, 15 Oct 2026): outlined link while the shop is open
                        (Register Now stays the only filled button); "● Spectators - Open <date>" while
                        upcoming; "● Spectators - Sold Out" when full — no waiting list for spectators,
                        the shop itself would only say "Sold out". */}
                    {specOpen && (
                        <SButtonLink
                            value={t('Spectator tickets')}
                            to={`${base}/spectators/registration`}
                            className={'w-full'}
                            outlined
                        />
                    )}
                    {(specSoon || specSoldOut) && (
                        <span className={slotRow}>
                            <StatusDot color={specColor} className="size-[7px]" />
                            <span>
                                <span className="text-[#808080]">{t('Spectators')} - </span>
                                {specLabel}
                            </span>
                        </span>
                    )}
                    {volOpen && (
                        <SButtonLink
                            value={t('Join Staff')}
                            to={`${base}/registration/volunteers`}
                            className={'w-full'}
                            outlined
                        />
                    )}
                    {volSoon && volSoonLabel && (
                        <span className={slotRow}>
                            <StatusDot color="--status-openat" className="size-[7px]" />
                            <span>
                                <span className="text-[#808080]">{t('Staff')} - </span>
                                {volSoonLabel}
                            </span>
                        </span>
                    )}
                </div>
            )}

            {/* Registration date */}
            {showRegDates && (
                <>
                    <SectionTitle className="mt-8" icon={<TicketSVG className="h-8 w-7" />}>
                        {t('Registration date')}
                    </SectionTitle>
                    <DateRange
                        className="mt-4"
                        startLabel={t('Starts')}
                        start={regStart}
                        endLabel={t('End')}
                        end={regEnd}
                    />
                </>
            )}

            {/* Qualifying for → next event (mock `.ev-qual`: meta 14 in --ink-2, 16px under the dates,
                the event name as a display-font link in strong ink) */}
            {qualifyingFor && (
                <div className="font-meta mt-4 flex items-center gap-2 text-sm text-[#5b5f6e]">
                    <TrophySVG className="size-4 flex-none text-[#272727]" />
                    <span>
                        {t('Qualifying for')} →{' '}
                        <Link to={qualifyingFor.to} className="font-display text-[#272727] hover:text-[#272727]">
                            {qualifyingFor.name}
                        </Link>
                    </span>
                </div>
            )}

            {/* Location */}
            <EventHubSidebarMap className="mt-8 max-nav:hidden" />
        </aside>
    )
}
